import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { BookOpen, Smartphone, Network, Shield } from 'lucide-react';
interface SlideMoatProps {
  onContinue: () => void;
}
export function SlideMoat({
  onContinue
}: SlideMoatProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  const moats = [{
    title: t('Medical Storytelling', 'Medicinsk historiefortælling'),
    desc: t('Codified pharma expertise that generalist AI cannot replicate.', 'Kodificeret pharma-ekspertise, som generalist-AI ikke kan kopiere.'),
    icon: <BookOpen className="w-6 h-6 text-teal-400" data-id="element-628" />,
    color: 'border-teal-400/30',
    bg: 'bg-teal-400/10'
  }, {
    title: t('Interactive Platform', 'Interaktiv platform'),
    desc: t('Short-format engagement driving measurable outcomes.', 'Kortformat-engagement, der driver målbare resultater.'),
    icon: <Smartphone className="w-6 h-6 text-purple-400" data-id="element-629" />,
    color: 'border-purple-500/30',
    bg: 'bg-purple-500/10'
  }, {
    title: t('Pharma Distribution', 'Pharma-distribution'),
    desc: t('Deep relationships and master agreements in a regulated vertical.', 'Dybdegående relationer og rammeaftaler i en reguleret vertikal.'),
    icon: <Network className="w-6 h-6 text-rose-400" data-id="element-630" />,
    color: 'border-rose-400/30',
    bg: 'bg-rose-400/10'
  }, {
    title: t('Behavioral Dataset', 'Adfærdsdatasæt'),
    desc: t('Privacy-safe data that compounds performance with every campaign.', 'Privatlivssikret data, der akkumulerer performance med hver kampagne.'),
    icon: <Database className="w-6 h-6 text-sky-400" data-id="element-631" />,
    color: 'border-sky-400/30',
    bg: 'bg-sky-400/10'
  }];
  const tintStyles = {
    rose: {
      bg: 'bg-rose-500/5',
      border: 'border-rose-400/30',
      iconBg: 'bg-rose-500/15',
      iconColor: 'text-rose-400'
    },
    teal: {
      bg: 'bg-teal-400/5',
      border: 'border-teal-400/30',
      iconBg: 'bg-teal-400/15',
      iconColor: 'text-teal-400'
    },
    blue: {
      bg: 'bg-blue-500/5',
      border: 'border-blue-400/30',
      iconBg: 'bg-blue-500/15',
      iconColor: 'text-blue-400'
    },
    violet: {
      bg: 'bg-violet-500/5',
      border: 'border-violet-400/30',
      iconBg: 'bg-violet-500/15',
      iconColor: 'text-violet-400'
    }
  } as const;
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const timers = moats.map((_, idx) => setTimeout(() => setStage(2 + idx), 800 + idx * 250));
    const t2 = setTimeout(() => setStage(2 + moats.length), 800 + moats.length * 250 + 200);
    const t3 = setTimeout(() => setStage(3 + moats.length), 800 + moats.length * 250 + 800);
    return () => {
      clearTimeout(t1);
      timers.forEach(clearTimeout);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-632">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-633">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-634">
          {t('The Moat', 'Voldgraven')}
        </h2>
        <p className="text-xl text-white/60" data-id="element-635">
          {t('Four compounding assets that defend the model.', 'Fire akkumulerende aktiver, der forsvarer modellen.')}
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center mt-10" data-id="element-636">
        {/* 4 pillar cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10" data-id="element-637">
          {moats.map((p, idx) => {
          const style = tintStyles[p.tint as keyof typeof tintStyles];
          return <motion.div key={p.title} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: stage >= 2 + idx ? 1 : 0,
            y: stage >= 2 + idx ? 0 : 20
          }} transition={{
            duration: 0.5
          }} className={`${style.bg} border ${style.border} rounded-2xl p-5 flex flex-col`} data-id="element-638">
                <div className={`w-10 h-10 rounded-xl ${style.iconBg} flex items-center justify-center ${style.iconColor} mb-5`} data-id="element-639">
                  {p.icon}
                </div>
                <h3 className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-640">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm mt-3 leading-snug" data-id="element-641">
                  {p.desc}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Lock + Unbreakable Flywheel */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        scale: stage >= 5 ? 1 : 0.9
      }} className="mt-12 bg-navy-900/50 border border-navy-700 rounded-2xl p-8 text-center relative overflow-hidden" data-id="element-642">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-400/5 via-purple-500/5 to-rose-400/5" data-id="element-643" />
          <div className="relative z-10" data-id="element-644">
            <div className="w-16 h-16 mx-auto bg-white/5 rounded-full flex items-center justify-center mb-4 border border-white/10" data-id="element-645">
              <Shield className="w-8 h-8 text-white" data-id="element-646" />
            </div>
            <h3 className="text-2xl font-serif text-white mb-2" data-id="element-647">
              {t('The Unbreakable Flywheel', 'Det ubrydelige svinghjul')}
            </h3>
            <p className="text-white/70 max-w-2xl mx-auto" data-id="element-648">
              {t('Together, these assets create a barrier to entry that no broad, generalist player can match inside this regulated vertical.', 'Sammen skaber disse aktiver en adgangsbarriere, som ingen bred, generalist-aktør kan matche inden for denne regulerede vertikal.')}
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 6 ? 1 : 0
      }} className="mt-12 flex justify-center" data-id="element-649">
          <Button onClick={onContinue} data-id="element-650">{t('Traction', 'Traktion')}</Button>
        </motion.div>
      </div>
    </div>;
}